'use client';
import { useDesign } from '@/context/DesignContext';

interface SocialLinksProps {
  links?: {
    instagram?: string;
    facebook?: string;
    tiktok?: string;
    twitter?: string;
    youtube?: string;
    linkedin?: string;
  };
  design?: any;
  style?: React.CSSProperties;
}

const defaultLinks = {
  instagram: 'https://instagram.com/',
  facebook: 'https://facebook.com/',
  tiktok: 'https://tiktok.com/',
  twitter: 'https://twitter.com/',
  youtube: 'https://youtube.com/',
  linkedin: 'https://linkedin.com/',
};

const iconMap: Record<string, string> = {
  instagram: '📸',
  facebook: '👥',
  tiktok: '🎵',
  twitter: '🐦',
  youtube: '▶️',
  linkedin: '🔗',
};

export default function SocialLinks({
  links = defaultLinks,
  design: propDesign,
  style,
}: SocialLinksProps) {
  const contextDesign = useDesign();
  const design = propDesign || contextDesign;

  const bgColor = design?.colors?.background || '#FFFFFF';
  const textColor = design?.colors?.text || '#1E1E1E';
  const textLight = design?.colors?.textLight || '#6B6B6B';
  const borderColor = design?.colors?.border || '#E8E4DA';
  const headingFont = design?.typography?.headingFont || "'Inter', sans-serif";
  const bodyFont = design?.typography?.bodyFont || "'Inter', sans-serif";
  const containerMaxWidth = design?.spacing?.containerMaxWidth || '1200px';
  const sectionPaddingTop = design?.spacing?.sectionPaddingTop || '60px';
  const sectionPaddingBottom = design?.spacing?.sectionPaddingBottom || '60px';

  // Filter out empty links
  const activeLinks = Object.entries(links).filter(([_, url]) => url && url.trim() !== '');

  if (activeLinks.length === 0) {
    return null; // Don't render if no social links are set
  }

  return (
    <section
      className="social-links-section"
      style={{
        background: bgColor,
        padding: `${sectionPaddingTop} 0 ${sectionPaddingBottom} 0`,
        ...style,
      }}
    >
      <div className="container" style={{ maxWidth: containerMaxWidth, margin: '0 auto', padding: '0 1.5rem' }}>
        <h2
          className="social-links-title"
          style={{
            fontFamily: headingFont,
            fontSize: '1.8rem',
            fontWeight: 600,
            color: textColor,
            marginBottom: '1.5rem',
            textAlign: 'center',
          }}
        >
          Suivez-nous
        </h2>
        <div
          className="social-links-grid"
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '2rem',
            flexWrap: 'wrap',
          }}
        >
          {activeLinks.map(([platform, url]) => (
            <a
              key={platform}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                background: design?.colors?.primary || '#1A4D8C',
                color: '#FFFFFF',
                textDecoration: 'none',
                fontFamily: bodyFont,
                fontSize: '0.9rem',
                transition: 'all 0.2s ease',
                boxShadow: design?.effects?.boxShadow || '0 2px 8px rgba(0,0,0,0.1)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = design?.effects?.boxShadow || '0 2px 8px rgba(0,0,0,0.1)';
              }}
            >
              <span style={{ fontSize: '1.2rem' }}>{iconMap[platform] || '🔗'}</span>
              <span style={{ textTransform: 'capitalize' }}>{platform}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}